import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiBaseUrlContext } from '../App';
import { ReactComponent as AddIcon } from '../assets/svg/icon-add.svg';
import { ReactComponent as RemoveItemIcon } from '../assets/svg/icon-remove-1.svg';
import { ReactComponent as FileIcon } from '../assets/svg/icon-file.svg';
import '../topmenu.css';
import '../forms.css';
import '../logpage.css';
import TechnicianDropdown from './TechnicianDropdown'; // Import your dropdown component

const LogPage = () => {
    const { id, logType } = useParams();
    const [logs, setLogs] = useState([]);
    const [newEntry, setNewEntry] = useState({ timestamp: '', description: '', files: [] , expectedReturnDate:''});
    const [error, setError] = useState(null);
    const [dragOver, setDragOver] = useState(false);
    const [files, setFiles] = useState([]);
    const [previews, setPreviews] = useState([]);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [selectedTechnician, setSelectedTechnician] = useState(null);
    const [datePickerContext, setDatePickerContext] = useState({ field: '', value: '' });
    const apiBaseUrl = useContext(ApiBaseUrlContext);
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const [itemName, setItemName] = useState([]);
    const [technicians, setTechnicians] = useState([]);
    const getCurrentDateTime = () => {
        const now = new Date();
        const offset = now.getTimezoneOffset() * 60000;
        const localISOTime = new Date(now - offset).toISOString().slice(0, 16);
        return localISOTime;
    };
    const getFutureDateTime = (daysToAdd) => {
        const now = new Date();
        now.setDate(now.getDate() + daysToAdd);
        const offset = now.getTimezoneOffset() * 60000;
        return new Date(now - offset).toISOString().slice(0, 16);
    };
    const [statusFlag, setStatusFlag] = useState('CHECKED_IN'); // Default value
    const [showAllLogs, setShowAllLogs] = useState(false);

    const fetchLogs = async () => {
        try {
            const response = await axios.get(`${apiBaseUrl}/mnt/api/inventory/logs/${id}`);
            console.log('Fetched logs and status_flag:', response.data); // ✅ Debug info
            setItemName(response.data.item_name);
            setStatusFlag(response.data.status_flag); // ✅ Update statusFlag from DB
    
            const fetchedLogs = response.data[logType];
            if (Array.isArray(fetchedLogs)) {
                setLogs(fetchedLogs);
                if (logType === 'checkin_checkout_log') {
                    const computedDescription = response.data.status_flag === 'CHECKED_IN' 
                        ? 'CHECKED_OUT' 
                        : 'CHECKED_IN';
                    setNewEntry((prev) => ({
                        ...prev,
                        description: computedDescription,
                        timestamp: getCurrentDateTime(),
                        expectedReturnDate: getFutureDateTime(7),
                    }));
                }
            } else {
                setError(t('logPage.errors.invalidLogData'));
            }
        } catch (error) {
            console.error('Error fetching logs:', error);
            setError(t('logPage.errors.fetchLogs'));
        }
    };
    
   
    useEffect(() => {
        if (!id || !logType) {
            setError(t('logPage.errors.invalidParams'));
            return;
        }
    
        setNewEntry((prev) => ({
            ...prev,
            timestamp: getCurrentDateTime(),
            expectedReturnDate: getFutureDateTime(7),
        }));
    
        const fetchTechnicians = async () => {
            try {
                const response = await axios.get(`${apiBaseUrl}/mnt/api/technicians`);
                setTechnicians(response.data);
            } catch (err) {
                console.error('Error fetching technicians:', err);
            }
        };
    
        fetchTechnicians();
        fetchLogs(); // ✅ Now using the globally defined fetchLogs
    }, [apiBaseUrl, id, logType, t]);

    

    const handleAddLog = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                setError(t('logPage.errors.authToken'));
                return;
            }
    
            // ✅ Determine the next action based on the current status from DB
            const computedDescription = statusFlag === 'CHECKED_IN' ? 'CHECKED_OUT' : 'CHECKED_IN';
    
            // ✅ Build the entry data
            const entryData = {
                ...newEntry,
                description: computedDescription,
                ...(newEntry.expectedReturnDate
                    ? { expectedReturnDate: new Date(newEntry.expectedReturnDate).getTime() }
                    : {}),
            };
    
            // ✅ Attach technician details if checking out
            if (logType === 'checkin_checkout_log' && computedDescription === 'CHECKED_OUT' && selectedTechnician) {
                entryData.checkout_by = selectedTechnician.username;
                entryData.userfirstname = selectedTechnician.userfirstname;
                entryData.userlastname = selectedTechnician.userlastname;
            }
    
            // ✅ Handle file uploads
            const formData = new FormData();
            formData.append('logType', logType);
            formData.append('entry', JSON.stringify(entryData));
            if (files.length > 0) {
                files.forEach((file) => formData.append('logFiles', file));
            }
    
            // ✅ Send the request
            const response = await axios.post(
                `${apiBaseUrl}/mnt/api/inventory/logs/${id}`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
    
            // ✅ Handle response & refresh state
            if (Array.isArray(response.data.updatedLog)) {
                setLogs(response.data.updatedLog);
                await fetchLogs(); // ✅ Always fetch latest status_flag after log entry
                setFiles([]);
                setPreviews([]);
                navigate(`/inventory`);
            } else {
                setError(t('logPage.errors.unexpectedResponse'));
            }
        } catch (error) {
            console.error(error);
            setError(t('logPage.errors.addLog'));
        }
    };
    
    
    

    const handleDatePickerSubmit = async () => {
        try {
            if (!datePickerContext.value) {
                alert(t('logPage.alerts.invalidDate'));
                return;
            }

            const epochTime = new Date(datePickerContext.value).getTime();

            await axios.put(`${apiBaseUrl}/mnt/api/inventory/${id}`, {
                [datePickerContext.field]: epochTime,
            });

            setShowDatePicker(false);
            setDatePickerContext({ field: '', value: '' });
        } catch (error) {
            setError('Error updating date');
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewEntry((prev) => ({ ...prev, [name]: value }));
    };
    const handleRemoveFile = (index) => {
        setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
        setPreviews((prevPreviews) => prevPreviews.filter((_, i) => i !== index));
    };
    
    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files);
        const newPreviews = selectedFiles.map((file) => URL.createObjectURL(file));
    
        setFiles((prev) => [...prev, ...selectedFiles]);
        setPreviews((prev) => [...prev, ...newPreviews]);
    };
    

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
    
        const droppedFiles = Array.from(e.dataTransfer.files);
        const newPreviews = droppedFiles.map((file) => URL.createObjectURL(file));
    
        setFiles((prevFiles) => [...prevFiles, ...droppedFiles]);
        setPreviews((prevPreviews) => [...prevPreviews, ...newPreviews]);
    };
    
    return (
        <>
            <div className="modal-full">
                {/* Top menu with Cancel button */}
                <div className="top-menu">
                    <div className="top-menu__button-space"></div>
                    <button
                        type="button"
                        className="button--grey"
                        onClick={() => navigate('/inventory')}
                    >
                        {t('logPage.buttons.cancel')}
                    </button>
                </div>
    
                {error && <div className="info-message">{error}</div>}
    
                {/* Log Title */}
                <div className="content__main-title">
                    {t(`logPage.titles.${logType}`)}{id} {itemName}
                </div>
    
                {/* Log Entries Section */}
                <div className="form-section__title">{t('logPage.sections.log')}</div>
                <div className="form-section">
                    {/* Handle Empty Logs */}
                    {logs.length === 0 ? (
                        <div className="entry-item entry-item__log">
                            <div className="entry-item__log-text">
                                {t('logPage.messages.noLogsFound')}
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Toggle Button for Collapsing/Expanding Logs */}
                            {logs.length > 1 && (
                                <button
                                    type="button"
                                    className="button--grey"
                                    onClick={() => setShowAllLogs((prev) => !prev)}
                                >
                                    {showAllLogs ? t('logPage.buttons.hideLogs') : t('logPage.buttons.showAllLogs')}
                                    {showAllLogs ? '➖' : '➕'}
                                </button>
                            )}

                            {/* Render Latest Log or All Logs Based on State */}
                            {(showAllLogs ? logs : [logs[logs.length - 1]]).map((log, index) => (
                                log && (
                                    <div key={index} className="entry-item entry-item__log">
                                        <div className="entry-item__log-text">
                                            <strong>{log.timestamp}</strong>: {log.description}
                                        </div>
                                        {log.files &&
                                            log.files.map((file, fileIndex) => (
                                                <div key={fileIndex} className="entry-item__log-file">
                                                    {file.filename && (
                                                        <a
                                                            href={`${apiBaseUrl}/mnt/api/uploads/machine/${file.filename}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="clickable-link"
                                                        >
                                                            View File
                                                        </a>
                                                    )}
                                                </div>
                                            ))}
                                        <span>{t('logPage.labels.loggedBy')} {log.username || ''}</span>
                                    </div>
                                )
                            ))}
                        </>
                    )}
                </div>

    
                {/* Add New Entry Section */}
                <div className="form-section__title">{t('logPage.sections.newEntry')}</div>
                    <div className="form-section">
                        <div className="entry-item entry-item__medium">
                            <label className="entry-label">{t('logPage.labels.timestamp')}</label>
                            <input
                                type="datetime-local"
                                name="timestamp"
                                value={newEntry.timestamp}
                                onChange={handleInputChange}
                            />
                        </div>
        
                        <div className="entry-item entry-item__medium">
                            <label className="entry-label">{t('logPage.labels.description')}</label>
                            <input
                                type="text"
                                name="description"
                                value={
                                    logType === 'checkin_checkout_log'
                                        ? statusFlag === 'CHECKED_IN'
                                            ? t('inventory.checkedOut')  // Next action: Check Out
                                            : t('inventory.checkedIn')   // Next action: Check In
                                        : newEntry.description
                                }
                                onChange={handleInputChange}
                                disabled={logType === 'checkin_checkout_log'} // ✅ Still disabled for auto-controlled logs
                            />
                        </div>

                            {logType === 'checkin_checkout_log' && statusFlag === 'CHECKED_IN' && (
                                <div className="entry-item entry-item__medium">
                                    <label className="entry-label">{t('TechnicianDropdown.pickTechnician')}</label>
                                    <select
                                        name="assigned_to"
                                        value={selectedTechnician ? selectedTechnician.userid : ''}
                                        onChange={(e) => {
                                            const selectedUser = technicians.find(
                                                (t) => t.userid.toString() === e.target.value
                                            );
                                            setSelectedTechnician(selectedUser);
                                        }}
                                        className="form-select-drop"
                                    >
                                        <option value="">{t('inventoryForm.dropdown.selectTechnician')}</option>
                                        {technicians.map((technician) => (
                                            <option key={technician.userid} value={technician.userid.toString()}>
                                                {`${technician.username} (${technician.userfirstname} ${technician.userlastname})`}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                                                                
                        {logType === 'checkin_checkout_log' && statusFlag === 'CHECKED_IN' && (
                            <div className="entry-item entry-item__medium">
                                <label className="entry-label">{t('logPage.labels.expectedReturnDate')}</label>
                                <input
                                    type="datetime-local"
                                    name="expectedReturnDate"
                                    value={newEntry.expectedReturnDate} // ✅ Controlled by state
                                    onChange={(e) =>
                                        setNewEntry({ ...newEntry, expectedReturnDate: e.target.value })
                                    }
                                />
                            </div>
                        )}

                  </div>
    
                {/* File Upload Section */}
                <div className="form-section__title">{t('logPage.sections.files')}</div>
                <div
                    className={`form-section ${
                        dragOver ? 'form-section--drag-over' : ''
                    }`}
                    onDrop={handleDrop}
                    onDragOver={(e) => {
                        e.preventDefault();
                        setDragOver(true);
                    }}
                    onDragLeave={() => setDragOver(false)}
                >
                    {/* Render the list of file previews */}
                    <div className="file-list">
                        {previews.length > 0 ? (
                            previews.map((preview, index) => (
                                <div key={index} className="file-preview-container">
                                    <img
                                        src={preview}
                                        alt={`Preview ${index}`}
                                        className="file-preview-image"
                                    />
                                    <button
                                        className="file-preview-remove"
                                        onClick={() => handleRemoveFile(index)}
                                    >
                                        {t('logPage.buttons.remove')}
                                    </button>
                                </div>
                            ))
                        ) : (
                            <div className="no-files-message">{t('logPage.messages.noFiles')}</div>
                        )}
                    </div>

                    {/* File input for manual file selection */}
                    <div className="file-upload">
                        <input
                            type="file"
                            id="files"
                            name="inventoryFiles"
                            className="file-upload__input"
                            multiple
                            onChange={handleFileChange}
                        />
                        <label htmlFor="files" className="file-upload__label">
                        <button
                            type="button"
                            className="button--icon button--grey"
                            onClick={() => document.getElementById('files').click()}
                        >
                            <AddIcon />
                            </button>
                            <span className="file-upload__filename">{t('logPage.labels.chooseFiles')}</span>
                        </label>
                    </div>
                </div>
                    
                {/* Save and Cancel Buttons at the Bottom */}
                <div className="form-submit">
                    <button
                        type="button"
                        className="button--blue"
                        onClick={handleAddLog}
                    >
                         {t('logPage.buttons.save')}
                    </button>
                    <div className="top-menu__button-space"></div>
                    <button
                        type="button"
                        className="button--grey"
                        onClick={() => navigate('/inventory')}
                    >
                        {t('logPage.buttons.cancel')}
                    </button>
                </div>
            </div>
        </>
    );
    
};

export default LogPage;